import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import type { Init, RefreshUser } from '@sorare/wallet-shared';
import { MessagingContext } from '@sorare/wallet-shared';

import { CenteredLoadingBall } from '#src/components/icons/CenteredLoadingBall.tsx';
import defaultDict from '#src/lib/dict.ts';

type User = Init['response']['result']['user'];
type Dict = Init['response']['result']['dict'];
type FeatureFlags = Init['response']['result']['featureFlags'];
type Info = Omit<Init['response']['result'], 'user' | 'dict' | 'featureFlags'>;

interface InfoContext {
  user: User;
  dict: Dict;
  info: Info;
  featureFlags: FeatureFlags;
  setUser: (user: User) => void;
  fetchUser: () => Promise<void>;
  logOut: () => void;
}

const Context = createContext<InfoContext | null>(null);

export const InfoProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<User | null>(null);
  const [dict, setDict] = useState<Dict | null>(null);
  const [info, setInfo] = useState<Info | null>(null);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({});
  const { sendRequest, registerHandler } = useContext(MessagingContext)!;

  const logOut = useCallback(() => setUser(undefined), []);

  const fetchUser = useCallback(async () => {
    // https://github.com/facebook/react/issues/34905
    return sendRequest<Init>('init', {}).then(({ result }) => {
      setUser(result.user);
      setDict(Object.assign(defaultDict, result.dict));
      setFeatureFlags(result.featureFlags);
      setInfo(result);
    });
  }, [sendRequest]);

  useEffect(() => {
    if (user === null) fetchUser();
  }, [fetchUser, user]);

  useEffect(
    () =>
      registerHandler<RefreshUser>('refreshUser', async () => {
        await fetchUser();

        return {};
      }),
    [fetchUser, registerHandler]
  );

  useEffect(() => {
    if (info?.langDir) {
      const html = window.document.documentElement;

      html.dir = info.langDir;
    }
  }, [info]);

  if (user === null || dict === null || info === null)
    return <CenteredLoadingBall />;

  return (
    <Context
      value={{ dict, featureFlags, info, user, setUser, logOut, fetchUser }}
    >
      {children}
    </Context>
  );
};

export default Context;
